import { Route, Routes as ReactRoutes } from "react-router-dom";
import Unauthorised from "domains/Misc/Unauthorised";
import Unknown from "domains/Misc/Unknown";
import Settings from "domains/Settings/Settings";
import ManageRegions from "domains/Settings/Regions/ManageRegions";
import AddRegion from "domains/Settings/Regions/AddRegion";
import HandleLogoutComponent from "components/Authentication/HandleLogout";

import Map from "domains/Map/Map";
import { RolesEnum } from "modules/rolesEnum";
import { CheckPermissions, IsNonSiteUser } from "modules/CheckPermissions";
import EditHwrcSite from "domains/Settings/HwrcSites/UpdateHwrcSite";
import { useStateMachine } from "little-state-machine";
import ManageHwrcSites from "domains/Settings/HwrcSites/ManageHwrcSites";
import AddHwrcSite from "domains/Settings/HwrcSites/AddHwrcSite";
import EditRegion from "domains/Settings/Regions/EditRegion";
import ManageNonWorkingPeriods from "domains/Settings/NonWorkingPeriods/ManageNonWorkingPeriods";
import UpdateNonWorkingPeriod from "domains/Settings/NonWorkingPeriods/UpdateNonWorkingPeriod";
import AddNonWorkingPeriod from "domains/Settings/NonWorkingPeriods/AddNonWorkingPeriod";
import ManageDisposalSites from "domains/Settings/DisposalSites/ManageDisposalSites";
import UpdateDisposalSite from "domains/Settings/DisposalSites/UpdateDisposalSite";
import AddDisposalSite from "domains/Settings/DisposalSites/AddDisposalSite";
import UpdateSystemSettings from "domains/Settings/System/UpdateSystemSettings";
import ContainerStatuses from "domains/Containers/ContainerStatuses/ContainerStatuses";
import SetFillThreshold from "domains/Settings/FillThreshold/SetFillThreshold";
import RaiseWorkOrder from "domains/WorkOrder/RaiseWorkOrder";
import ManageSla from "domains/Settings/Sla/ManageSla";
import EditSla from "domains/Settings/Sla/EditSla";
import EditDriverWorkingHours from "domains/Settings/Sla/EditDriverWorkingHours";
import ManageCollectors from "domains/Settings/Collectors/ManageCollectors";
import AddVehicle from "domains/Settings/Collectors/Vehicles/AddVehicle";
import EditVehicle from "domains/Settings/Collectors/Vehicles/EditVehicle";
import EditProvider from "domains/Settings/Collectors/Providers/EditProvider";
import AddProvider from "domains/Settings/Collectors/Providers/AddProvider";
import WorkOrderStatuses from "domains/WorkOrder/WorkOrderStatuses/WorkOrderStatuses";
import ViewWorkOrder from "domains/WorkOrder/ViewWorkOrder/ViewWorkOrder";
import ModifyWorkOrder from "domains/WorkOrder/ModifyWorkOrder/ModifyWorkOrder";
import CompleteWorkOrder from "domains/WorkOrder/CompleteWorkOrder/CompleteWorkOrder";
import SiteDiaryLandingPage from "domains/SiteDiary/SiteDiaryLandingPage";
import ItemOrderStatuses from "domains/ItemOrders/ItemOrderStatuses/ItemOrderStatuses";
import CreateItemOrder from "domains/ItemOrders/CreateItemOrder";
import AddAuthorisedVisitorTemporaryWorker from "domains/SiteDiary/AuthorisedVisitorsTemporaryWorkers/AddAuthorisedVisitorTemporaryWorker";
import EditAuthorisedVisitorTemporaryWorker from "domains/SiteDiary/AuthorisedVisitorsTemporaryWorkers/EditAuthorisedVisitorTemporaryWorker";
import EditItemOrder from "domains/ItemOrders/EditItemOrder";
import AddStaffOnSite from "domains/SiteDiary/StaffOnSite/AddStaffOnSite";
import EditStaffOnSite from "domains/SiteDiary/StaffOnSite/EditStaffOnSite";
import AddAuthorisedMaintenance from "domains/SiteDiary/AuthorisedMaintenance/AddAuthorisedMaintenance";
import EditAuthorisedMaintenance from "domains/SiteDiary/AuthorisedMaintenance/EditAuthorisedMaintenance";
import AddEventAndIncident from "domains/SiteDiary/EventsAndIncidents/AddEventAndIncident";
import EditEventAndIncident from "domains/SiteDiary/EventsAndIncidents/EditEventAndIncident";
import { RoutePathsEnum } from "./RoutePathsEnum";

const getUserRole = () : RolesEnum => {
  const { state } = useStateMachine();
  return state.userRole!;
};

const Routes = () => (
  <ReactRoutes>
    <Route path={RoutePathsEnum.Home} element={<ContainerStatuses />} />
    <Route path={RoutePathsEnum.Containers} element={<ContainerStatuses />} />
    <Route
      path={RoutePathsEnum.ItemOrders}
      element={<ItemOrderStatuses />}
    />
    <Route
      path={RoutePathsEnum.ItemOrdersCreate}
      element={<CreateItemOrder />}
    >
      <Route path=":id/:date" element={<CreateItemOrder />} />
    </Route>
    <Route
      path={RoutePathsEnum.ItemOrdersEdit}
      element={<EditItemOrder />}
    >
      <Route path=":id/:date" element={<EditItemOrder />} />
    </Route>
    <Route path={RoutePathsEnum.WorkOrders} element={<WorkOrderStatuses />} />
    <Route
      path={RoutePathsEnum.WorkOrderRaise}
      element={IsNonSiteUser(getUserRole()) ? <RaiseWorkOrder /> : <Unauthorised />}
    />
    <Route path={`${RoutePathsEnum.WorkOrderView}/:id`}>
      <Route path=":source/:sourceType/:sourceId" element={<ViewWorkOrder />} />
      <Route path=":source/:sourceType" element={<ViewWorkOrder />} />
      <Route path=":source" element={<ViewWorkOrder />} />
      <Route path="" element={<ViewWorkOrder />} />
    </Route>

    <Route path={`${RoutePathsEnum.WorkOrderModify}/:id`}>
      <Route path=":source/:sourceType/:sourceId" element={IsNonSiteUser(getUserRole()) ? <ModifyWorkOrder /> : <Unauthorised />} />
      <Route path=":source/:sourceType" element={IsNonSiteUser(getUserRole()) ? <ModifyWorkOrder /> : <Unauthorised />} />
      <Route path=":source" element={IsNonSiteUser(getUserRole()) ? <ModifyWorkOrder /> : <Unauthorised />} />
      <Route path="" element={IsNonSiteUser(getUserRole()) ? <ModifyWorkOrder /> : <Unauthorised />} />
    </Route>

    <Route path={`${RoutePathsEnum.WorkOrderComplete}/:id`}>
      <Route path=":source/:sourceType/:sourceId" element={<CompleteWorkOrder />} />
      <Route path=":source/:sourceType" element={<CompleteWorkOrder />} />
      <Route path=":source" element={<CompleteWorkOrder />} />
      <Route path="" element={<CompleteWorkOrder />} />
    </Route>

    <Route
      path={RoutePathsEnum.Settings}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <Settings /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsRegions}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <ManageRegions /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsRegionsAdd}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin]) ? <AddRegion /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsRegionsUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin]) ? <EditRegion /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsHwrcSites}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <ManageHwrcSites /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsHwrcSitesAdd}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <AddHwrcSite /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsHwrcSitesUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <EditHwrcSite /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsNonWorkingPeriods}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <ManageNonWorkingPeriods /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsNonWorkingPeriodsAdd}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <AddNonWorkingPeriod /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsNonWorkingPeriodsUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <UpdateNonWorkingPeriod /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsDisposalSites}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <ManageDisposalSites /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsDisposalSitesAdd}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <AddDisposalSite /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsDisposalSitesUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <UpdateDisposalSite /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsSystemSettings}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin]) ? <UpdateSystemSettings /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsSetThreshold}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <SetFillThreshold /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsSla}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <ManageSla /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsSlaUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <EditSla /> : <Unauthorised />}
    />
    <Route
      path={RoutePathsEnum.SettingsSlaDwhUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <EditDriverWorkingHours /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsCollectors}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <ManageCollectors /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsCollectorsVehiclesAdd}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <AddVehicle /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsCollectorsVehiclesUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <EditVehicle /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsCollectorsProvidersAdd}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <AddProvider /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SettingsCollectorsProvidersUpdate}
      element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin]) ? <EditProvider /> : <Unauthorised />}
    />

    <Route
      path={RoutePathsEnum.SiteDiaryLandingPage}
      element={<SiteDiaryLandingPage />}
    >
      <Route
        path={`${RoutePathsEnum.SiteDiaryLandingPage}/:hwrcSiteIdQs/:diaryDate`}
        element={<SiteDiaryLandingPage />}
      />
    </Route>

    <Route
      path={`${RoutePathsEnum.AuthorisedVisitorTemporaryWorkerAdd}/:hwrcSiteId/:diaryDate`}
      element={<AddAuthorisedVisitorTemporaryWorker />}
    />

    <Route
      path={`${RoutePathsEnum.AuthorisedVisitorTemporaryWorkerEdit}/:hwrcSiteId/:diaryDate/:authorisedVisitorTemporaryWorkerId`}
      element={<EditAuthorisedVisitorTemporaryWorker />}
    />

    <Route
      path={`${RoutePathsEnum.SiteDiaryAddStaffOnSite}/:hwrcSiteId/:diaryDate`}
      element={<AddStaffOnSite />}
    />

    <Route
      path={`${RoutePathsEnum.SiteDiaryEditStaffOnSite}/:hwrcSiteId/:diaryDate`}
      element={<EditStaffOnSite />}
    />

    <Route
      path={`${RoutePathsEnum.AuthorisedMaintenanceAdd}/:hwrcSiteId/:diaryDate`}
      element={<AddAuthorisedMaintenance />}
    />

    <Route
      path={`${RoutePathsEnum.AuthorisedMaintenanceEdit}/:hwrcSiteId/:diaryDate/:authorisedMaintenanceId`}
      element={<EditAuthorisedMaintenance />}
    />

    <Route
      path={`${RoutePathsEnum.SiteDiaryAddEventsAndIncidents}/:hwrcSiteId/:diaryDate`}
      element={<AddEventAndIncident />}
    />

    <Route
      path={`${RoutePathsEnum.SiteDiaryEditEventsAndIncidents}/:hwrcSiteId/:diaryDate/:eventIncidentId`}
      element={<EditEventAndIncident />}
    />

    <Route path={RoutePathsEnum.Map}>
      <Route
        path=":source/:sourceType/:sourceId"
        element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin, RolesEnum.LogisticsTeam]) ? <Map /> : <Unauthorised />}
      />
      <Route
        path=":source/:sourceType"
        element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin, RolesEnum.LogisticsTeam]) ? <Map /> : <Unauthorised />}
      />
      <Route
        path=":source"
        element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin, RolesEnum.LogisticsTeam]) ? <Map /> : <Unauthorised />}
      />
      <Route
        path=""
        element={CheckPermissions(getUserRole(), [RolesEnum.SuperAdmin, RolesEnum.Admin, RolesEnum.LogisticsTeam]) ? <Map /> : <Unauthorised />}
      />
    </Route>

    <Route path={RoutePathsEnum.Unauthorised} element={<Unauthorised />} />
    <Route path={RoutePathsEnum.Logout} element={<HandleLogoutComponent />} />
    <Route path="*" element={<Unknown />} />
  </ReactRoutes>
);

export default Routes;
